import { Link } from "react-router-dom";
import { Plane, GraduationCap, FileText, Building2, Wallet, HeartPulse, CloudSnow, Briefcase, Landmark, BusFront, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import { ImageBand } from "@/components/visuals";
import CompareLedger from "@/components/CompareLedger";
import { Reveal, CountUp } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { VS_LOCAL_BOARDS } from "@/data/whyEgos";
import { COUNTRIES } from "@/data/countries";
import { UNIVERSITIES, PRICING, SCHOOL } from "@/data/content";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const INTL_STATS = [
  { value: 20, suffix: "+", label: "countries our students already study from" },
  { value: 890, prefix: "$", suffix: "", label: "CAD per credit, all-inclusive" },
  { value: 365, suffix: "", label: "days a year you can start your courses" },
  { value: 110, suffix: " hrs", label: "of guided learning in every credit" },
];

const JOURNEY = [
  { n: "1", title: "Enrol from your home country", text: "Register online any day of the year and start immediately — studying online from home requires no study permit and no travel." },
  { n: "2", title: "Earn your OSSD credits", text: "Complete the Grade 12 University-level courses your target programs demand (most Canadian universities base admission on your best six 4U/M credits)." },
  { n: "3", title: "Apply to universities", text: "Our guidance team helps you build your list and apply through OUAC or directly — transcripts go out electronically the week you finish." },
  { n: "4", title: "Receive your acceptance letter", text: "Your offer arrives with tuition details and the deadline calendar. This letter is the document your study permit application is built around." },
  { n: "5", title: "Apply for your study permit", text: "Apply to IRCC with your acceptance letter, proof of funds, biometrics and any requested medicals. Requirements change often." },
  { n: "6", title: "Arrive, study, work, stay", text: "Work up to 24 hours a week off campus during terms, full-time during scheduled breaks, and explore post-graduation work permits and permanent-residence pathways." },
];

const LIVING = [
  { icon: Wallet, title: "What life costs", text: "Most international students budget CAD $14,000–$25,000 per year for rent, food, phone and transit, depending upon lifestyle." },
  { icon: Building2, title: "Where you'll live", text: "First year, most students pick university residence or a homestay; after that, shared apartments near campus. Many Ontario universities guarantee residence for first-year applicants who accept early." },
  { icon: HeartPulse, title: "Health insurance", text: "Most provinces do not cover international students. In Ontario, universities arrange mandatory plans such as UHIP — typically about CAD $50–$100 a month, bundled with your tuition." },
  { icon: CloudSnow, title: "The famous winters", text: "Yes, they are real: expect snow between December and March. Every campus is built for it — indoor tunnels, heated walkways — and a proper coat becomes your best first purchase." },
  { icon: Briefcase, title: "Working while studying", text: "Your study permit allows up to 24 hours of off-campus work per week during terms and full-time during scheduled breaks — a realistic way to offset living costs." },
  { icon: Landmark, title: "Banking & paperwork", text: "Canadian banks offer no-fee student accounts, and you can open one before you arrive. Once in Canada, you will get a Social Insurance Number — required for any job." },
  { icon: BusFront, title: "Getting around", text: "Cities run on safe transit with student discounts — Presto in Ontario. No car needed in Toronto, Ottawa, Waterloo or Montreal." },
  { icon: GraduationCap, title: "After the bachelor's", text: "A completed Canadian degree opens work permits and permanent-residence pathways such as the Canadian Experience Class. Eligibility depends on your university's designation — confirm the current IRCC rules before you accept an offer." },
];

const INTL_FAQ = [
  {
    category: "Studying with EGOS",
    items: [
      { q: "Do I need a study permit to study with EGOS?", a: "No. Studying online from your home country requires no study permit and no travel — you only apply for a permit when you are accepted to a Canadian university for your bachelor's degree." },
      { q: "What do I need to enrol?", a: "A copy of your passport, transcripts from your current or previous school (translated if needed), and the online application. Your teacher will assess where you stand and map the credits you still need." },
      { q: "How many credits do I need?", a: "International students typically complete the six Grade 12 University-level (4U/M) credits their program requires — sometimes fewer if your previous schooling transfers. We assess this with you before you start." },
    ],
  },
  {
    category: "English & Admissions Tests",
    items: [
      { q: "Do I need IELTS or TOEFL?", a: "Most Canadian universities require proof of English proficiency from international applicants, even with an OSSD English credit. Plan to write IELTS, TOEFL or CAEL alongside your courses, and check each university's minimum score early." },
      { q: "What about other entrance requirements?", a: "Some programs ask for supplementary essays, portfolios or interviews. Our guidance team reviews your target programs with you so nothing is a surprise in your final year." },
    ],
  },
  {
    category: "University & Visas",
    items: [
      { q: "Which universities accept the OSSD?", a: "All Canadian universities accept it — it is the standard Ontario credential. It is also recognized by universities in the United States, the United Kingdom, Australia and beyond." },
      { q: "Can EGOS help with my applications?", a: "Yes. Course planning, program prerequisites, OUAC submission and transcript delivery are all part of the service, at no extra cost." },
      { q: "Can I work in Canada after graduating?", a: "After your degree, post-graduation work permits and permanent-residence pathways may be open to you. These rules depend on your university's designation and change regularly — confirm the current policy on the IRCC website before accepting an offer." },
    ],
  },
];

const MONEY = [
  { label: "EGOS tuition", value: `$${PRICING.international}`, note: "CAD per credit — teachers, materials, tutoring and exam included" },
  { label: "Bachelor's tuition", value: "$10k–$50k", note: "typical international range per year at Canadian universities" },
  { label: "Living costs", value: "$14k–$25k", note: "typical per year for a single student, depending upon lifestyle" },
];

const VS_ADMISSIONS = {
  cols: ["The OSSD with EGOS", "High School from Your Home Country"],
  accent: "aqua",
  rows: [
    { title: "Local Application Stream", egos: "Enables international student to apply like a local student to Canadian universities via Group A Stream (formerly OUAC 101).", other: "They apply in the same pool as everyone else from all over the world in Group B Stream (formerly OUAC 105)." },
    { title: "English language requirements", egos: "Native English-medium study — completing your courses satisfies English requirements for various universities, building academic fluency while reducing the need for external tests.", other: "Mandatory testing — non-English curriculums require separate IELTS or TOEFL sittings." },
    { title: "Flexibility and Zero Relocation Costs", egos: "Home-based study — 100% online with no moving and no visa fees. Also allows preparation for other competitive exams with full control on your study schedule.", other: "Daily physical attendance required, with rigid requirements at your local brick-and-mortar school." },
    { title: "Recognition & Portability", egos: "A ministry-inspected OSSD with identical standing to any publicly inspected Ontario school — recognized by universities worldwide.", other: "Local credentials vary in how easily they transfer to Canadian and international university admissions." },
    { title: "Eligibility Guaranteed", egos: "University matching — course codes mirror exact university entry requirements (MCV4U for engineering, and so on), so you arrive eligible for competitive majors like Computer Science or Engineering.", other: "Curriculum gaps — foreign syllabi often miss required units, triggering conditional offers or rejections." },
    { title: "Native Grade Valuation", egos: "Direct percentage scoring (0–100%) — the exact grading scale Canadian university admissions use natively. Your marks are evaluated directly, with no conversion penalties eating into your performance.", other: "Foreign conversion loss — local scores must be mapped through external tables (like WES) that often devalue them." },
    { title: "Streamlined Processing", egos: "A standardized Canadian credential processed through OUAC and reviewed against familiar Canadian academic standards — faster admission decisions on a file universities already understand.", other: "A complex international evaluation involving extra checks, verifications and manual reviews before decisions." },
    { title: "Transcript Delivery", egos: "Direct electronic uploads — an inspected school pushes your course codes straight to university portals, eliminating delays, missing-document holds and lost-transcript risks.", other: "Manual paperwork — request physical transcripts, arrange translations and rely on international mailing." },
    { title: "Low-Stress Grading", egos: "Continuous 70/30 assessment — 70% coursework, only 30% final evaluation. Consistent control over your final mark across the whole course instead of one exam day.", other: "High-stakes exams — 80–100% of the grade can hinge on a single, high-pressure exam day." },
    { title: "Top-Six GPA Focus", egos: "Admissions weigh your top six Grade 12 courses — ENG4U plus your best five relevant ones. Spend your energy only on the courses that build your competitive edge.", other: "A broad average that factors in non-essential subjects or national exams that can drag your overall GPA down." },
  ],
};

const UNI_BEYOND = {
  excellence:
    "The OSSD is a prestigious credential that serves as a powerful bridge to global higher education. Earning it online demonstrates self-discipline and academic readiness — traits admissions officers value worldwide. While outcomes depend on individual effort, this pathway typically supports long-term goals such as international careers and future immigration interests.",
  bullets: [
    "Strengthened university applications for Canadian and global institutions.",
    "Development of critical research and independent study skills.",
    "A recognized academic foundation for international career opportunities.",
  ],
  destDesc:
    "OSSD is a globally recognized diploma. Graduates successfully meet the rigorous credential standards for admission to leading institutions across these key regions.",
  destinations: [
    { region: "Canada", unis: ["University of Toronto", "University of Waterloo", "McGill University", "McMaster University", "Western University", "Queen's University", "UBC", "University of Alberta"] },
    { region: "United States", unis: ["Harvard University", "Yale University", "Princeton University", "Stanford University", "Columbia University", "MIT", "UCLA"] },
    { region: "United Kingdom", unis: ["University of Oxford", "University of Cambridge", "Imperial College London", "UCL"] },
    { region: "Oceania", unis: ["University of Melbourne", "University of Sydney", "University of Auckland"] },
  ],
};

function UniversityBeyondSection() {
  return (
    <section className="container-x pt-20 sm:pt-28 lg:pt-32 pb-14 sm:pb-16 lg:pb-20" data-testid="intl-university-beyond">
      <SectionHeading
        align="center"
        eyebrow="Your Destination"
        title={<>Empowering global futures with <span className="font-serif italic font-medium text-brand">OSSD excellence</span></>}
        desc="Unlock doors to top-tier universities worldwide through a flexible, accredited Ontario high school diploma program designed for the modern international student."
      />
      <div className="mx-auto mt-14 max-w-3xl">
        <Reveal>
          <div className="card-line bg-white p-8 sm:p-10" data-testid="uni-beyond-excellence">
            <h3 className="font-display text-lg font-bold text-ink">University excellence &amp; beyond</h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">{UNI_BEYOND.excellence}</p>
            <ul className="mt-5 space-y-3">
              {UNI_BEYOND.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-aqua" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section className="container-x pb-20 sm:pb-28 lg:pb-32" data-testid="intl-destinations-section">
      <div className="text-center">
        <Reveal>
          <h3 className="font-display text-[26px] font-extrabold tracking-tight text-ink sm:text-[31px]">
            Global destination <span className="font-serif italic font-medium text-brand">pathways</span>
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">{UNI_BEYOND.destDesc}</p>
        </Reveal>
        <div className="mt-10 grid gap-5 text-left sm:grid-cols-2 lg:grid-cols-4">
          {UNI_BEYOND.destinations.map((d, i) => (
            <Reveal key={d.region} delay={i * 0.06} className="h-full">
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6" data-testid={`uni-beyond-region-${i}`}>
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-brand">{d.region}</p>
                <ul className="mt-4 space-y-2">
                  {d.unis.map((u) => (
                    <li key={u} className="flex items-center gap-2 text-sm font-semibold text-ink">
                      <span className="h-1 w-1 flex-shrink-0 rounded-full bg-brand" />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function International() {
  return (
    <>
      <PageHero
        eyebrow="International Students"
        lines={[<>From anywhere on Earth,</>, <span className="font-serif italic font-medium text-brand">to a Canadian campus</span>]}
        desc="Earn the Ontario Secondary School Diploma online from your home country, then apply with your OSSD for streamlined admission at a Canadian university for your bachelor's degree — with help at every stage from admission at EGOS to applying at university."
        testid="international-page-heading"
      />

      <section className="border-b border-slate-200/70 bg-sand/60 py-10" data-testid="intl-stats-band">
        <div className="container-x grid grid-cols-2 gap-8 lg:grid-cols-4">
          {INTL_STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.07}>
              <p className="font-mono text-3xl font-bold text-ink">
                {"prefix" in s && s.prefix}
                <CountUp to={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-500">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="intl-admissions-compare-section">
        <SectionHeading
          align="center"
          eyebrow="For University Admissions"
          title={<>Why the OSSD with EGOS beats <span className="whitespace-nowrap font-serif italic font-medium text-brand">your local school board</span></>}
          desc="10 strategic advantages when your goal is admission at a Canadian university — or any university abroad."
          eyebrowClassName="!text-[22px] !tracking-[0.2em]"
        />
        <div className="mt-14">
          <CompareLedger data={VS_ADMISSIONS} testid="intl-admissions-compare" />
        </div>
      </section>

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="intl-journey-section">
        <SectionHeading
          eyebrow="The Journey"
          title={<>Six steps from your sofa to <span className="font-serif italic font-medium text-brand">a Canadian degree</span></>}
          desc="This is the exact sequence our international graduates follow — including the visa reality, not just the brochure."
        />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {JOURNEY.map((j, i) => (
            <Reveal key={j.n} delay={(i % 3) * 0.07} className="h-full">
              <div className="card-line relative h-full p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`intl-step-${j.n}`}>
                <span className="absolute right-6 top-6 font-mono text-4xl font-bold text-slate-100">{j.n}</span>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-brandlight">
                  {j.n === "5" ? <FileText className="h-5 w-5" /> : j.n === "6" ? <Plane className="h-5 w-5" /> : <GraduationCap className="h-5 w-5" />}
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-ink">{j.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{j.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="intl-countries-section">
        <SectionHeading
          eyebrow="Starting Points"
          title={<>Joining us from China, India, Nigeria <span className="font-serif italic font-medium text-brand">or the Philippines?</span></>}
          desc="Four of our largest student communities — here is what your transcripts look like to us and the credits students from your country usually take."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {COUNTRIES.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.07} className="h-full">
              <div className="card-line flex h-full flex-col p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`country-card-${c.name.toLowerCase()}`}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Starting point</p>
                <h3 className="mt-2 font-display text-xl font-bold text-ink">{c.name}</h3>
                <div className="mt-5 space-y-4 text-sm leading-relaxed">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Your transcripts</p>
                    <p className="mt-1 text-slate-600">{c.from}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Typical EGOS path</p>
                    <p className="mt-1 text-slate-600">{c.path}</p>
                  </div>
                  <div className="mt-auto rounded-xl bg-sand/70 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-brand">Good to know</p>
                    <p className="mt-1 text-slate-600">{c.note}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="intl-upgrade-section">
        <SectionHeading
          eyebrow="Upgrade Pathways"
          title={<>What the journey <span className="font-serif italic font-medium text-brand">looks like practically</span></>}
          desc="An illustrative example of how an international student reaches a Canadian offer through EGOS — no study permit needed until the acceptance letter arrives."
        />
        <Reveal className="mt-12">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid="intl-upgrade-card">
            <div className="grid md:grid-cols-12">
              <div className="bg-ink p-8 text-ivory sm:p-10 md:col-span-5">
                <p className="eyebrow !text-brandlight">Example · Chandigarh → Toronto</p>
                <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight">
                  From CBSE classroom to a <span className="font-serif italic font-medium text-brandlight">U of T offer in six months</span>
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-ivory/70">
                  Aaditya finished Grade 11 (CBSE) and started the OSSD program along with his CBSE Class 12. PLAR
                  gave him 22 credits directly, and he completed 8 credits with EGOS, one IELTS sitting, one OUAC
                  application — all from home.
                </p>
                <p className="mt-6 font-mono text-2xl font-bold text-brandlight">8 credits · 6 months · 1 offer</p>
                <p className="mt-2 text-xs text-ivory/50">Illustrative example based on typical pathways.</p>
              </div>
              <div className="flex flex-col justify-center gap-5 p-8 sm:p-10 md:col-span-7">
                {[
                  { n: "1", t: "Enrolled from home", d: "ENG4U, MCV4U, MHF4U, SBI4U, SCH4U, PSK4U, OLC4O and ENG3U — no study permit required to start." },
                  { n: "2", t: "IELTS alongside coursework", d: "Prepared and sat the test while studying — band 7.5." },
                  { n: "3", t: "Applied with our guidance team", d: "OUAC application filed, transcripts sent the week his final exam was marked." },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-sand/40 p-5" data-testid={`intl-upgrade-step-${s.n}`}>
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand font-mono text-xs font-bold text-white">{s.n}</span>
                    <div>
                      <p className="font-display text-sm font-bold text-ink">{s.t}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.d}</p>
                    </div>
                  </div>
                ))}
                <p className="rounded-xl bg-aqualight/60 p-4 text-sm font-semibold text-cyan-800" data-testid="intl-upgrade-result">
                  Result: Offer of Admission — Life Sciences, University of Toronto
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section id="ossd-vs-local" className="border-y border-slate-200/70 bg-sand/50 py-20 sm:py-28 lg:py-32" data-testid="intl-compare-section">
        <div className="container-x">
          <SectionHeading
            eyebrow="The Diploma Decision"
            title={<>EGOS <span className="font-serif italic font-medium text-brand">vs.</span> Your Local School<span className="font-serif italic font-medium text-brand">, for High School Studies</span></>}
            desc="Why finishing high school online with us is the smarter route into Canada than relying on local boards at home."
          />
          <div className="mt-14 rounded-3xl bg-white/60 p-2 sm:p-6">
            <CompareLedger data={VS_LOCAL_BOARDS} testid="intl-compare" />
          </div>
        </div>
      </section>

      <ImageBand
        src="https://images.unsplash.com/photo-1758270705518-b61b40527e76?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        alt="A diverse group of students studying together online"
        tag="Your future campus starts here"
        testid="international-image-band"
      />

      <UniversityBeyondSection />

      <section className="container-x pt-14 sm:pt-16 lg:pt-20 pb-12 sm:pb-14 lg:pb-16" data-testid="intl-universities-section">
        <SectionHeading
          align="center"
          title={<>All Canadian universities <span className="font-serif italic font-medium text-brand">respect the OSSD</span></>}
        />
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          {UNIVERSITIES.map((u, i) => (
            <Reveal key={u} delay={i * 0.05}>
              <span className="rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-ink" data-testid={`intl-university-${i}`}>
                {u}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <DestinationsSection />

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="intl-money-section">
        <SectionHeading
          eyebrow="Money & Numbers"
          title={<>What your pathway costs, <span className="font-serif italic font-medium text-brand">for university degree</span></>}
          desc="Three numbers to plan around. Our fee is the only one we control — the rest are realistic planning ranges."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {MONEY.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.08} className="h-full">
              <div className={`h-full rounded-2xl p-7 ${i === 0 ? "border border-brand/40 bg-brandlight/70" : "card-line"}`} data-testid={`intl-money-card-${i}`}>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{m.label}</p>
                <p className="mt-3 font-display text-4xl font-extrabold text-ink">{m.value}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600" data-testid="intl-money-disclaimer">
            <Wallet className="h-4 w-4 flex-shrink-0 text-brand" />
            Immigration and tuition figures change regularly. Verify current requirements on the IRCC website and with each university's international admissions office before you commit.
          </div>
        </Reveal>
      </section>

      <section className="bg-ink py-20 sm:py-28 lg:py-32" data-testid="intl-living-section">
        <div className="container-x">
          <SectionHeading
            dark
            eyebrow="Living in Canada"
            title={<>The practical guide to <span className="font-serif italic font-medium text-brandlight">moving, studying and living here</span></>}
            desc="The things students actually ask us about once the acceptance letter arrives."
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {LIVING.map((l, i) => (
              <Reveal key={l.title} delay={(i % 4) * 0.06} className="h-full">
                <div className="h-full rounded-2xl border border-ivory/15 bg-ink2/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand/60" data-testid={`intl-living-card-${i + 1}`}>
                  <l.icon className="h-5 w-5 text-brand" />
                  <h3 className="mt-4 font-display text-base font-bold text-ivory">{l.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-ivory/60">{l.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-sand/50 py-20 sm:py-28 lg:py-32" data-testid="intl-faq-section">
        <div className="container-x">
          <SectionHeading
            eyebrow="International FAQ"
            title={<>Visas, English tests, transcripts — <span className="font-serif italic font-medium text-brand">answered</span></>}
          />
          <div className="mx-auto mt-12 max-w-3xl space-y-10">
            {INTL_FAQ.map((cat, ci) => (
              <div key={cat.category} data-testid={`intl-faq-category-${ci}`}>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-brand">{cat.category}</p>
                <Reveal delay={0.06} className="mt-4">
                  <Accordion type="single" collapsible className="card-line bg-white px-6" data-testid={`intl-faq-accordion-${ci}`}>
                    {cat.items.map((f, i) => (
                      <AccordionItem key={i} value={`${ci}-${i}`}>
                        <AccordionTrigger className="text-left font-display text-base font-semibold text-ink" data-testid={`intl-faq-item-${ci}-${i}`}>
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-20" data-testid="intl-cta-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-ink px-8 py-16 text-center sm:px-16">
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-aqua/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-brand/20 blur-3xl" />
            <p className="eyebrow !text-brandlight">Your move</p>
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-4xl">
              Your degree starts with a credit<span className="font-serif italic font-medium text-brand"> — not with a visa.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-ivory/60">
              Start online from home today. Questions first? Write to {SCHOOL.email} and an advisor who works with international students every week will reply.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/admissions" data-testid="intl-cta-apply" className="btn-accent">Apply in <span className="text-ink">5 minutes</span></Link>
              <Link to="/contact" data-testid="intl-cta-contact" className="btn-navy-outline">Talk to an <span className="text-brand">advisor</span></Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
